import type { CreaseAssignment, CreasePattern } from '@/origami/types';
import { planarize } from '@/origami/planarize';
import { assignmentFromAngle } from '@/origami/angles';
import { toRadians, clamp, MAX_FOLD_ANGLE } from '@/constants/numeric';
import type { FoldFile, FoldEdgeAssignment } from './types';
import { resolveFrame } from './parser';
import { validateFold, type FoldValidationIssue } from './validator';

const VALID = new Set<FoldEdgeAssignment>(['B', 'M', 'V', 'F', 'U', 'C', 'J']);

export interface FoldImportResult {
  pattern: CreasePattern | null;
  issues: FoldValidationIssue[];
  /** Fields we did not interpret but will preserve on export. */
  preserved: Record<string, unknown>;
}

/**
 * FOLD frame -> CreasePattern.
 *
 * Angles arrive in degrees (FOLD) and leave in radians (Fold). The sign
 * convention is identical, so no negation is involved anywhere - see
 * src/origami/angles.ts.
 *
 * The imported edges are always re-planarised: other tools happily emit crossing
 * edges, and every downstream layer here assumes a planar embedding.
 */
export function foldToPattern(file: FoldFile, frameIndex = 0): FoldImportResult {
  const frame = resolveFrame(file, frameIndex);
  const issues = validateFold(frame);
  if (issues.some((i) => i.severity === 'error')) {
    return { pattern: null, issues, preserved: {} };
  }

  const coords = (frame.vertices_coords ?? []).map((c) => ({ x: c[0], y: c[1] }));
  const edges = (frame.edges_vertices ?? []) as [number, number][];
  const rawAssign = frame.edges_assignment ?? [];
  const rawAngles = frame.edges_foldAngle ?? [];

  const assignments: CreaseAssignment[] = [];
  const angles: (number | null)[] = [];
  for (let e = 0; e < edges.length; e++) {
    const degrees = rawAngles[e];
    const radians =
      degrees === null || degrees === undefined || !Number.isFinite(degrees)
        ? null
        : clamp(toRadians(degrees), -MAX_FOLD_ANGLE, MAX_FOLD_ANGLE);
    let a = rawAssign[e];
    if (!a || !VALID.has(a)) {
      a = radians === null ? 'U' : (assignmentFromAngle(radians) as FoldEdgeAssignment);
    }
    assignments.push(a as CreaseAssignment);
    angles.push(radians);
  }

  const preserved: Record<string, unknown> = {};
  const known = new Set([
    'file_spec', 'file_creator', 'file_author', 'file_title', 'file_description',
    'file_classes', 'file_frames', 'frame_title', 'frame_description', 'frame_author',
    'frame_classes', 'frame_attributes', 'frame_unit', 'vertices_coords',
    'vertices_vertices', 'vertices_faces', 'edges_vertices', 'edges_assignment',
    'edges_foldAngle', 'edges_length', 'edges_faces', 'faces_vertices', 'faces_edges',
  ]);
  for (const [k, v] of Object.entries(frame)) {
    if (!known.has(k)) preserved[k] = v;
  }

  const { pattern, report } = planarize({
    verticesCoords: coords,
    edgesVertices: edges,
    edgesAssignment: assignments,
    edgesFoldAngle: angles,
    meta: {
      title: frame.frame_title ?? file.file_title ?? 'Imported FOLD',
      author: frame.frame_author ?? file.file_author,
      description: frame.frame_description ?? file.file_description,
      unit: frame.frame_unit,
      source: 'FOLD import',
    },
  });

  if (report.splitEvents > 0) {
    issues.push({
      severity: 'warning',
      message: `${report.splitEvents} crossing or touching edge pair(s) were split to make the graph planar.`,
    });
  }
  if (report.duplicateEdgesMerged > 0) {
    issues.push({
      severity: 'warning',
      message: `${report.duplicateEdgesMerged} duplicate edge(s) were merged.`,
    });
  }

  return { pattern, issues, preserved };
}
